import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Dictionary } from 'src/app/core/helper/util';
import { DateTimeHelper } from 'src/app/core/helper/datetime.helper';
import { AppSettings } from 'src/app/core/configs/app.settings';

@Injectable({
  providedIn: 'root'
})
export abstract class HttpService {

  protected endpoint: string;
  private headers: HttpHeaders = new HttpHeaders({ 'Content-Type': 'application/json' });

  private cache: Dictionary<any> = {};
  constructor(private http: HttpClient) {
    this.headers = new HttpHeaders();
    this.headers = this.headers.set('Content-Type', 'Application/Json');
    this.endpoint = AppSettings.apiURL
  }

  get httpClient() { return this.http; }
  get httpHeaders() { return this.headers; }
  get apiURL() { return AppSettings.apiURL; }


  public clearCache() {
    this.cache = {};
  }

  async getById(id: number, endpoint: string = this.endpoint) {
    try {
      return await this.doFetch(`${endpoint}/${id}`);
    } catch (error) {
      throw error;
    }
  }

  async fetchValue<T>(controller: string, endpoint: string = this.endpoint, responseType?: string) {
    const ep = `${this.apiURL}/${controller}${endpoint}`;
    const response = await this.doFetch(ep, responseType);

    if (Array.isArray(response)) {
      return (response as T[]);
    } else {
      return response as T;
    }
  }

  async fetch(endpoint: string = this.endpoint, responseType?: string) {
    if (endpoint == null) {
      console.error('Cannot fetch. No endpoint provided.');
    } else if (!endpoint.startsWith('http')) {
      endpoint = `${this.apiURL}/${endpoint}`;
    }
    return this.doFetch(endpoint, responseType);
  }

  async save(entity: any, endpoint: string = this.endpoint, responseType?: string) {
    try {
      const result = (this.isUpdate(entity)) ?
        await this.put(entity, this.getUpdateUrl(entity, endpoint), (responseType || 'text')) :
        await this.post(entity, endpoint, (responseType || 'json'));
      return result || entity;
    } catch (error) {
      throw error;
    }
  }

  async upload(formData: FormData, endpoint: string = this.endpoint) {
    return new Promise((resolve, reject) => {
      this.http.post(endpoint, formData, { headers: new HttpHeaders(), responseType: 'json' }).subscribe(
        response => { resolve(response); },
        error => { reject(this.getError(error)); }
      );
    });
  }

  post(data?: any, endpoint: string = this.endpoint, responseType: any = 'json') {
    if (endpoint == null) {
      console.error('Cannot fetch. No endpoint provided.');
    } else if (!endpoint.startsWith('http')) {
      endpoint = `${this.apiURL}/${endpoint}`;
    }
    return new Promise((resolve, reject) => {
      this.http.post(endpoint, data, { headers: this.headers, responseType }).subscribe(
        response => { resolve(response); },
        error => { reject(this.getError(error)); }
      );
    });
  }

  put(data?: any, endpoint: string = this.endpoint, responseType: any = 'text') {
    return new Promise((resolve, reject) => {
      this.http.put(endpoint, data, { headers: this.headers, responseType }).subscribe(
        response => { resolve(response); },
        error => { reject(this.getError(error)); }
      );
    });
  }


  deleteItem(id: number, endpoint: string = this.endpoint, responseType: any = 'text') {
    return this.delete(`${endpoint}/${id}`, responseType);
  }

  delete(endpoint: string = this.endpoint, responseType: any = 'text') {
    return new Promise((resolve, reject) => {
      this.http.delete(endpoint, { headers: this.headers, responseType }).subscribe(
        data => { resolve(data); },
        error => { reject(this.getError(error)); }
      );
    });
  }



  protected addHeader(key: string, value: string) {
    this.headers = (this.headers.has(key)) ? this.headers.set(key, value) : this.headers.append(key, value);
  }

  protected removeHeader(key: string) {
    if (this.headers.has(key)) {
      this.headers = this.headers.delete(key);
    }
  }

  protected clearHeaders() {
    this.headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  }

  protected getError(error: HttpErrorResponse, defaultVal?: string) {
    // Set the default value
    let message;
    console.error('Http Error: ', error);

    // Attempt to parse the JSON
    try {
      if (error.error == null) {
        if (error.status == null) {
          message = error.message;
        } else {
          switch (error.status) {
            case 0: message = 'Unable to reach server'; break;
            case 404: message = 'Not found'; break;
            case 401:
            case 403: message = 'Session Expired. Please logout and then log back in.'; break;
            case 500: message = 'Server error, please contact support'; break;
            case 503: message = 'Server unavailable, please try again in a few minutes'; break;
            default: message = defaultVal;
          }
        }
      } else if (error.error != null) {
        message = (typeof error.error === 'string') ? JSON.parse(error.error).message : message = error.error.message;
      }
    } catch (exception) {
      console.error(exception);
    }
    // the response
    const response = message || defaultVal;

    // Store the error in local storage
    // const errLog: any[] = JSON.parse(localStorage.getItem('error-log') || '[]');
    // errLog.push({ error, response, timestamp: DateTimeHelper.formatDate(new Date()) });
    // localStorage.setItem('error-log', JSON.stringify(errLog));

    // Return the value
    return response;
  }

  protected canCache(): boolean {
    return false;
  }

  private doFetch(endpoint: string = this.endpoint, responseType: any = 'json'): Promise<any> {
    if (this.canCache() && this.cache[endpoint] != null) {
      return new Promise((resolve, reject) => {
        resolve(this.cache[endpoint]);
      });
    }
    return new Promise((resolve, reject) => {
      
      
      // if the endpoint ends with a slash, remove it
      if (endpoint.endsWith('/')) {
        endpoint = endpoint.substring(0, endpoint.length - 1);
      }
      
      // if there are double slashes somewhere other than after the http://, make them into 1
      if (endpoint.indexOf('//') > -1 ) {
        if (endpoint.startsWith('https') ){
          var restOfUrl = endpoint.substring(8);
          restOfUrl = restOfUrl.replace(/\/\//g, '/');
          endpoint = 'https://' + restOfUrl;
        } else if (endpoint.startsWith('http') ){
          var restOfUrl = endpoint.substring(7);
          restOfUrl = restOfUrl.replace(/\/\//g, '/');
          endpoint = 'http://' + restOfUrl;
        } else {
          // if the endpoint contains a double slash or ends with a slash, remove it
          endpoint = endpoint.replace(/\/\//g, '/');
        }
      }
      

      this.http.get(endpoint, { headers: this.headers, responseType }).subscribe(
        data => {
          if (this.canCache()) {
            this.cache[endpoint] = data;
          }
          resolve(data);
        },
        error => { reject(this.getError(error, 'Error getting data')); }
      );
    });
  }

  protected abstract isUpdate(entity: any): boolean;
  protected abstract getUpdateUrl(entity: any, endpoint: string): string;
}
